import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Checkbox,
} from '@mui/material';
import { ImageField, useNotify } from 'react-admin';
import { putJson } from '../../utils/api';

type EditGiftCardDialogProps = {
  open: boolean;
  onClose: () => void;
  refresh: Function;
  giftCard: {
    id: string;
    name: string;
    rpRate: number | null;
    isVisible: number;
    assets: {
      giftCardUrl: string;
    };
  };
  onSave: (updatedGiftCard: {
    id: string;
    rpRate: number | null;
    isVisible: number;
  }) => void;
};

export const EditGiftCardDialog: React.FC<EditGiftCardDialogProps> = ({
  open,
  onClose,
  giftCard,
  onSave,
  refresh,
}) => {
  const [rpRate, setRpRate] = useState<number | null>(giftCard.rpRate);
  const [isVisible, setIsVisible] = useState(giftCard.isVisible);
  const [rpRateEnabled, setRpRateEnabled] = useState(giftCard.rpRate !== null); // Checkbox state
  const notify = useNotify();

  const handleSave = async () => {
    try {
      const updatedRpRate = rpRateEnabled ? rpRate : null;

      await putJson(`/admin/gift-card/${giftCard.id}`, {
        rpRate: updatedRpRate,
        isVisible,
      });

      onSave({ id: giftCard.id, rpRate: updatedRpRate, isVisible });
      notify('Gift card updated successfully', { type: 'success' });
      onClose();
    } catch (error) {
      console.error('Failed to update gift card', error);
      notify('Failed to update gift card', { type: 'error' });
    } finally {
      refresh();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <ImageField
            source="assets.giftCardUrl"
            record={giftCard}
            className="gift-card-img"
            label="Image"
          />
          <Typography variant="h6" mt={2}>
            {giftCard.name}
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={rpRateEnabled}
              onChange={(e) => setRpRateEnabled(e.target.checked)}
            />
          }
          label="Set RP Rate"
        />

        <TextField
          label="RP Rate"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          fullWidth
          value={rpRate !== null ? rpRate : ''}
          onChange={(e) => setRpRate(Number(e.target.value))}
          margin="normal"
          disabled={!rpRateEnabled}
        />

        <FormControlLabel
          control={
            <Switch
              checked={Boolean(isVisible)}
              onChange={(e) => setIsVisible(Number(e.target.checked))}
            />
          }
          label="Is Visible"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
